import type { ReactNode } from 'react';

/**
 * Skeleton loading placeholder · per Stage C §3 S1=A.
 *
 * Three flavors:
 * - `<Skeleton.Line />` · single block · width/height props
 * - `<Skeleton.Card />` · ticket-card skeleton (head pill + title + meta + avatar)
 * - `<Skeleton.Widget />` · widget shell skeleton (rings outline + pills outline)
 *
 * Pulse animation respects `prefers-reduced-motion` via CSS rule in globals.css.
 */

export interface SkeletonLineProps {
  width?: number | string;
  height?: number | string;
  rounded?: 'pill' | 'card' | 'tight' | 'snug';
  className?: string;
  'aria-label'?: string;
}

function SkeletonLine({ width = '100%', height = 12, rounded = 'tight', className, ...rest }: SkeletonLineProps) {
  const radius =
    rounded === 'pill' ? 50 : rounded === 'card' ? 8 : rounded === 'snug' ? 6 : 4;
  return (
    <span
      className={`skeleton-line${className ? ` ${className}` : ''}`}
      style={{ width, height, borderRadius: radius }}
      aria-hidden="true"
      {...rest}
    />
  );
}

function SkeletonCard() {
  return (
    <div className="skeleton-card" role="status" aria-busy="true" aria-live="polite">
      <span className="sr-only">Загрузка</span>
      <div className="skeleton-card__head">
        <SkeletonLine width={56} height={16} rounded="pill" />
        <SkeletonLine width={72} height={12} />
      </div>
      <SkeletonLine width="92%" height={14} />
      <SkeletonLine width="68%" height={14} />
      <div className="skeleton-card__meta">
        <SkeletonLine width={48} height={10} />
        <SkeletonLine width={36} height={10} />
        <SkeletonLine width={28} height={10} />
        <SkeletonLine width={20} height={20} rounded="pill" className="skeleton-line--avatar" />
      </div>
    </div>
  );
}

function SkeletonList({ count = 3 }: { count?: number }) {
  return (
    <div className="skeleton-list">
      {Array.from({ length: count }, (_, i) => (
        <SkeletonCard key={i} />
      ))}
    </div>
  );
}

function SkeletonWidget({ children }: { children?: ReactNode }) {
  return (
    <div className="skeleton-widget" role="status" aria-busy="true" aria-live="polite">
      <span className="sr-only">Загрузка</span>
      {children ?? (
        <>
          <SkeletonLine width="40%" height={16} />
          <div className="skeleton-widget__row">
            <SkeletonLine width={56} height={56} rounded="pill" />
            <SkeletonLine width={56} height={56} rounded="pill" />
            <SkeletonLine width={56} height={56} rounded="pill" />
          </div>
        </>
      )}
    </div>
  );
}

export const Skeleton = Object.assign(SkeletonCard, {
  Line: SkeletonLine,
  Card: SkeletonCard,
  List: SkeletonList,
  Widget: SkeletonWidget,
});
